import {
  type DynamicClient,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import { assertDefined } from '@dynamic-labs-sdk/client/core';
import type { TransactionReceipt } from 'viem';

import type { EvmWalletAccount } from '../../EvmWalletAccount.types';
import { createPublicClientFromNetworkData } from '../../viem/createPublicClientFromNetworkData';
import { approveErc20 } from '../approveErc20';
import { getErc20Allowance } from '../getErc20Allowance';

type ApproveErc20IfRequiredAndWaitReceiptParams = {
  amount: string;
  spenderAddress: string;
  tokenAddress: string;
  walletAccount: EvmWalletAccount;
};

/**
 * Approves an ERC-20 token if the current allowance is insufficient and waits for the transaction receipt.
 *
 * @param params.amount - The amount of tokens to approve.
 * @param params.ownerAddress - The address that granted the allowance.
 * @param params.spenderAddress - The address allowed to spend the tokens.
 * @param params.tokenAddress - The ERC-20 token contract address.
 * @param params.walletAccount - The EVM wallet account used to resolve the active network.
 * @param client - The Dynamic client instance.
 * @returns The transaction receipt if an approval was sent, otherwise undefined.
  * @not-instrumented
 */
export const approveErc20IfRequiredAndWaitReceipt = async (
  {
    amount,
    spenderAddress,
    tokenAddress,
    walletAccount,
  }: ApproveErc20IfRequiredAndWaitReceiptParams,
  client: DynamicClient
): Promise<{ receipt: TransactionReceipt } | undefined> => {
  const { networkData } = await getActiveNetworkData({ walletAccount }, client);

  assertDefined(
    networkData,
    `No network data found for wallet account ${walletAccount.address}`
  );

  const currentAllowance = await getErc20Allowance(
    {
      networkData,
      ownerAddress: walletAccount.address,
      spenderAddress,
      tokenAddress,
      walletAccount,
    },
    client
  );

  const approvalAmount = BigInt(amount);

  if (currentAllowance >= approvalAmount) {
    return undefined;
  }

  const result = await approveErc20(
    {
      amount: approvalAmount,
      spenderAddress,
      tokenAddress,
      walletAccount,
    },
    client
  );
  const approvalTransactionHash = result.transactionHash;

  const publicClient = createPublicClientFromNetworkData({ networkData });

  const receipt = await publicClient.waitForTransactionReceipt({
    hash: approvalTransactionHash as `0x${string}`,
  });

  return { receipt };
};
